import _toConsumableArray from "@babel/runtime-corejs3/helpers/toConsumableArray";
import React, { useMemo } from 'react';
import { Icon, BarLoader } from '@unite-us/ui';
import { Button } from '@unite-us/app-components';
import findPrimaryOrFirst from '@unite-us/client-utils/dist/utils/arrays/findPrimaryOrFirst';
import { addresses } from '@unite-us/client-utils';
import { get, isEmpty } from 'lodash';
import getDobData from './utils/getDOBData';
import formatPhoneNumber from './utils/formatPhoneNumber';
import filterMilitaryInfo from './utils/filterMilitaryInfo';
import formatAddress from './utils/formatAddress';
import formatHouseholdCount from './utils/formatHouseholdCount';
import { dollarAmount } from './utils/dollarAmount';
import generateClientData from './utils/generateClientData';
import getConsentStatusDisplay from './utils/getConsentStatusDisplay';
import findStatusConfigs from './utils/findStatusConfigs';
import { usePerson } from '../../hooks/usePerson';
import { useConsentCommunications } from '../../hooks/useConsentCommunications';
import { ClientHeaderProvider } from '../../context/clientHeader';
import ContactRequirementsBanner from './ContactRequirementsBanner';
var LITE_MODE_ITEMS = ['dob', 'tel', 'email'];
function ConsentStatus(_ref) {
  var status = _ref.status,
    enums = _ref.enums;
  var placeholder = getConsentStatusDisplay(status, get(enums, 'consent.contact_status'));
  var statusConfig = findStatusConfigs(status);
  return /*#__PURE__*/React.createElement("div", {
    className: "color-background--".concat(statusConfig.color, "-color py-2 px-3 flex items-center rounded h-full")
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: statusConfig.icon,
    color: "#FFF"
  }), /*#__PURE__*/React.createElement("p", {
    className: "ml-2 text-white uppercase"
  }, placeholder));
}

/**
 * @typedef {Object} AppState
 * @property {Object} enums
 * @property {boolean} isMilitaryFocused
 * @property {string} personId
 * @property {string} providerId
 */

/**
 * @typedef {Object} Adapters
 * @property {Object} coreApi
 */

/**
 * @typedef {Object} ClientHeaderProps
 * @property {AppState} appState
 * @property {Adapters} adapters
 * @property {'full'|'lite'} mode - The mode of the client header.
 * - full: Displays all information
 * - lite: Displays only full_name, consent status, contact preferences, dob, phone number, email address
 */

/**
 * Client Header microapp for displaying client's information on top of pages.
 *
 * @param {ClientHeaderProps} props
 */
export default function ClientHeader(props) {
  return /*#__PURE__*/React.createElement(ClientHeaderProvider, {
    value: props
  }, /*#__PURE__*/React.createElement(InnerClientHeader, props));
}
function InnerClientHeader(_ref2) {
  var appState = _ref2.appState,
    mode = _ref2.mode,
    _ref2$callbacks = _ref2.callbacks,
    callbacks = _ref2$callbacks === void 0 ? {} : _ref2$callbacks;
  var enums = appState.enums,
    _appState$isMilitaryF = appState.isMilitaryFocused,
    isMilitaryFocused = _appState$isMilitaryF === void 0 ? true : _appState$isMilitaryF,
    _appState$hbh1509Clie = appState.hbh1509ClientsDrawerP2Enabled,
    hbh1509ClientsDrawerP2Enabled = _appState$hbh1509Clie === void 0 ? false : _appState$hbh1509Clie;
  var _onClick = callbacks === null || callbacks === void 0 ? void 0 : callbacks.onClientNameClick;
  var person = usePerson();
  var shouldFetch = !(appState.crtb854ForwardingSuperset && appState.isForwardReferral);
  var _useConsentCommunicat = useConsentCommunications({
      shouldFetch: shouldFetch
    }),
    areConsentCommLoaded = _useConsentCommunicat.areLoaded,
    hasConsentComm = _useConsentCommunicat.hasSome;
  var items = useMemo(function () {
    if (!person) return [];
    var phoneNumber = findPrimaryOrFirst(person.phone_numbers).phone_number;
    var emailAddress = findPrimaryOrFirst(person.email_addresses).email_address;
    var address = addresses.findMainAddress(person.addresses);
    var militaryInfo = filterMilitaryInfo(person, enums, isMilitaryFocused);
    var values = [generateClientData('dob', getDobData(person.date_of_birth)), generateClientData('tel', formatPhoneNumber(phoneNumber)), generateClientData('email', emailAddress), generateClientData('address', formatAddress(address)), generateClientData('household', formatHouseholdCount(get(person, 'household.total'))), generateClientData('household monthly income', dollarAmount(person.gross_monthly_income))];
    return [].concat(values, _toConsumableArray(militaryInfo)).filter(function (item) {
      return !isEmpty(item.value);
    });
  }, [person, enums, isMilitaryFocused]);
  var modeItems = useMemo(function () {
    return mode === 'lite' ? items.filter(function (item) {
      return LITE_MODE_ITEMS.includes(item.label);
    }) : items;
  }, [items, mode]);
  var isLoaded = person && (!shouldFetch || areConsentCommLoaded);
  var consentStatus = get(person, "consent.state");
  if (!consentStatus && hasConsentComm) {
    consentStatus = 'pending';
  }
  var content = isLoaded ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center"
  }, hbh1509ClientsDrawerP2Enabled ? /*#__PURE__*/React.createElement(Button, {
    className: "mr-3 text-4xl text-dark-blue hover:underline hover:text-action-blue p-0 font-regular-font",
    link: true,
    onClick: function onClick() {
      return _onClick(person);
    },
    label: person.full_name
  }) : /*#__PURE__*/React.createElement("h2", {
    className: "mr-3 text-4xl text-dark-blue"
  }, person.full_name), /*#__PURE__*/React.createElement(ConsentStatus, {
    status: consentStatus,
    enums: enums
  })), /*#__PURE__*/React.createElement(ContactRequirementsBanner, {
    contact_preference: person.contact_preference,
    enums: enums
  }), /*#__PURE__*/React.createElement("ul", {
    className: "list-unstyled"
  }, modeItems.map(function (item, index) {
    return /*#__PURE__*/React.createElement("li", {
      key: item.label,
      className: "inline-block"
    }, index > 0 ? /*#__PURE__*/React.createElement("span", {
      className: "mx-2 border-r border-solid border-dark-pale-blue"
    }) : null, /*#__PURE__*/React.createElement("h3", {
      className: "inline mr-1 text-lg uppercase ui-h3-heavy"
    }, item.label), /*#__PURE__*/React.createElement("p", {
      className: "inline text-lg"
    }, item.value));
  }))) : /*#__PURE__*/React.createElement("div", {
    className: "space-y-2"
  }, /*#__PURE__*/React.createElement(BarLoader, {
    tall: true
  }), /*#__PURE__*/React.createElement(BarLoader, null), /*#__PURE__*/React.createElement(BarLoader, null));
  return /*#__PURE__*/React.createElement("div", {
    className: "px-12 py-6 border-b border-solid border-dark-pale-blue"
  }, content);
}
//# sourceMappingURL=index.js.map